import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState, Subtask } from 'constants/interfaces'
import { AppDispatch } from 'config/redux'
import keys from 'constants/keys'
import { updateResults } from 'redux/actions/tasks_v2'
import { getFileExtension, openInCloudViewer } from 'utils'
import { isExternalFormLink } from 'utils/jotformLink'
import { findResultForMutation, resolveResultOccurrence } from '../../model/resultRequests'
import { getFormattedLink, isServiceManagedLink } from '../model/link'
import { isCourseLink } from '../model/courseProgress'
import { useTaskOverviewContext } from '../TaskOverviewStackProvider'
import { isResultCompleted } from '../model/results'

interface ILinkSession {
  scope: string
  active: boolean
  pending: boolean
  popup: Window | null
}

export function useExecutionLink(subtask: Subtask) {
  const { resultContextId, isReadMode, task, isSubtaskBusy, setIsSubtaskBusy } = useTaskOverviewContext()
  const dispatch: AppDispatch = useDispatch()
  const config = useSelector((state: RootState) => state.config)
  const { tasks, userResults, allResults } = useSelector((state: RootState) => state.tasks_v2)
  const uid = useSelector((state: RootState) => state.auth.uid)

  const [isOpening, setIsOpening] = useState(false)
  const [error, setError] = useState<'open' | 'save' | null>(null)
  // a SCORM course opens in the in-task viewer (ExecutionCourseViewer), not in a browser tab
  const [isViewerOpen, setIsViewerOpen] = useState(false)
  const sessionRef = useRef<ILinkSession | null>(null)
  const viewerOpenRef = useRef(false)

  const occurrence = resolveResultOccurrence(task.recurrence_ts)
  const scope = JSON.stringify([
    subtask.tid,
    subtask.st_id,
    resultContextId,
    occurrence,
    subtask.external_link,
    isReadMode,
  ])
  const serviceManaged = isServiceManagedLink(subtask)
  const course = isCourseLink(subtask)

  const open = async () => {
    const session = sessionRef.current
    if (
      !session ||
      session.scope !== scope ||
      !session.active ||
      session.pending ||
      isSubtaskBusy ||
      isReadMode ||
      !subtask.external_link
    )
      return

    session.pending = true
    setIsOpening(true)
    setError(null)
    setIsSubtaskBusy(true)
    let stage: 'open' | 'save' = 'open'
    let openedViewer = false

    try {
      if (course) {
        openedViewer = await openCourse(session)
        return
      }
      const integrated = isExternalFormLink(subtask.external_link)
      const result = findResultForMutation(allResults?.[subtask.tid], subtask.st_id, resultContextId, occurrence)
      // The service may already have completed an initialized form while this cache still says INPROGRESS.
      const hasServiceResult = serviceManaged && result?.status === keys.TASK_STATUSES.INPROGRESS.value
      const shouldSave =
        !hasServiceResult && (!isResultCompleted(result) || result?.status === keys.TASK_STATUSES.SKIPPED.value)
      const { tags, first_name, last_name, default_group, groups, rid } = config.config
      const formattedUrl = getFormattedLink({
        link: subtask.external_link,
        tasks,
        task,
        tid: subtask.tid,
        st_id: subtask.st_id,
        contextId: resultContextId,
        uid: uid!,
        userResults,
        tags,
        first_name,
        last_name,
        default_group,
        groups,
        activeGroupID: config.activeGroupID!,
        rid,
        // Integrated forms open only after the result exists on the server, like Mobile.
        isFirstResult: integrated ? false : undefined,
      })
      const url = keys.extensionsForCloudViewer.includes(getFileExtension(formattedUrl))
        ? openInCloudViewer(formattedUrl)
        : formattedUrl

      if (integrated && shouldSave) {
        // Reserve the tab during the click so awaiting persistence cannot trigger a popup blocker.
        session.popup = window.open('', '_blank')
        if (!session.popup) {
          setError('open')
          return
        }
        session.popup.opener = null
      } else {
        window.open(url, '_blank', 'noopener,noreferrer')
      }

      if (!shouldSave) return

      stage = 'save'
      const saved = await dispatch(
        updateResults({
          tid: subtask.tid,
          st_id: subtask.st_id,
          type: subtask.type,
          resultContextId,
          recurrence_ts: occurrence,
          status: serviceManaged ? keys.TASK_STATUSES.INPROGRESS.value : keys.TASK_STATUSES.DONE.value,
          reservations: { reasons: [], score_deduction: 0 },
          optimistic: false,
        })
      )
      if (!session.active) return
      if (!saved) {
        setError('save')
        return
      }

      stage = 'open'
      if (session.popup && !session.popup.closed) {
        session.popup.location.replace(url)
        session.popup = null
      }
    } catch {
      if (session.active) setError(stage)
    } finally {
      session.popup?.close()
      session.popup = null
      if (session.active) {
        session.pending = false
        setIsOpening(false)
        // an open course viewer stays busy until closeViewer
        if (!openedViewer) setIsSubtaskBusy(false)
      }
    }
  }

  /**
   * A course: like other service-managed links its result is first set IN_PROGRESS (the server marks it
   * DONE from the course's progress), then it opens in the viewer. Nothing about the user goes in its url.
   */
  const openCourse = async (session: ILinkSession): Promise<boolean> => {
    const result = findResultForMutation(allResults?.[subtask.tid], subtask.st_id, resultContextId, occurrence)
    const hasServiceResult = result?.status === keys.TASK_STATUSES.INPROGRESS.value
    const shouldSave =
      !hasServiceResult && (!isResultCompleted(result) || result?.status === keys.TASK_STATUSES.SKIPPED.value)

    if (shouldSave) {
      const saved = await dispatch(
        updateResults({
          tid: subtask.tid,
          st_id: subtask.st_id,
          type: subtask.type,
          resultContextId,
          recurrence_ts: occurrence,
          status: keys.TASK_STATUSES.INPROGRESS.value,
          reservations: { reasons: [], score_deduction: 0 },
          optimistic: false,
        })
      )
      if (!session.active) return false
      if (!saved) {
        setError('save')
        return false
      }
    }
    if (!session.active) return false
    viewerOpenRef.current = true
    setIsViewerOpen(true)
    return true
  }

  // the viewer keeps the subtask busy (no pager/Done) until the course is closed
  const closeViewer = () => {
    viewerOpenRef.current = false
    setIsViewerOpen(false)
    setIsSubtaskBusy(false)
  }

  useEffect(() => {
    const session: ILinkSession = { scope, active: true, pending: false, popup: null }
    sessionRef.current = session
    setIsOpening(false)
    setError(null)
    setIsViewerOpen(false)

    return () => {
      session.active = false
      session.popup?.close()
      session.popup = null
      if (session.pending || viewerOpenRef.current) setIsSubtaskBusy(false)
      viewerOpenRef.current = false
    }
  }, [scope, setIsSubtaskBusy])

  return { open, isOpening, error, serviceManaged, isViewerOpen, closeViewer }
}
